import React from 'react';

import {
  ItemOption,
  ItemOptionContent,
  StatusTag,
} from '@components/common/AutoCompleteInput/styled';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { EStringOp } from '@src/emp-3.0/types';

export interface IMappedStatusRowData {
  op: EStringOp;
  value: string;
}

export const escapeRegExp = (string) => {
  // $& indicates a match to the matching substring
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const isMapped = (name?: string, mappedData?: IMappedStatusRowData[]) => {
  if (!name) return false;
  return (mappedData ?? []).some((dataItem) => {
    if (!dataItem) return false;
    const { op, value } = dataItem;
    switch (op) {
      case EStringOp.EQ:
      case EStringOp.NOT_EQ:
        return value === name;
      case EStringOp.CONTAINS:
        return name?.includes(value);
      case EStringOp.NOT_CONTAINS:
        return !name?.includes(value);
      default:
        return false;
    }
  });
};

export const isDeployed = (name?: string, deployedData?: any[]) => {
  if (!name) return false;
  return (deployedData ?? []).some((dataItem) => {
    if (!dataItem) return false;
    return dataItem.status === 1 && dataItem.name === name;
  });
};

export const buildOptionsWithTag = (
  options: any[],
  dimensionKey?: string,
  searchText?: string | null,
  mappedData?: IMappedStatusRowData[],
  blockedData?: string[],
  showEllipsis = true,
  disabledMappedData = false,
  extraAllowedEventNames?: string[],
  extraDisabledEventNames?: string[] | RegExp,
) => {
  if (options.length === 0 || dimensionKey === undefined) return [];
  const filterMappedData = mappedData?.filter(
    (data) => !extraAllowedEventNames?.includes(data.value),
  );
  const filterBlockedData = (blockedData ?? []).filter(
    (name) => !extraAllowedEventNames?.includes(name),
  );

  return options.map((data) => {
    const rowDataName = data[dimensionKey] ?? '';
    let extraDisabledEventName: boolean;
    if (extraDisabledEventNames instanceof RegExp) {
      extraDisabledEventName = extraDisabledEventNames.test(rowDataName);
    } else {
      extraDisabledEventName = !!extraDisabledEventNames?.includes(rowDataName);
    }

    const showMapped = isMapped(rowDataName, mappedData);
    const showBlocked = (blockedData ?? []).includes(rowDataName);
    const disabledMappedEvent = extraAllowedEventNames
      ? isMapped(rowDataName, filterMappedData)
      : showMapped;
    const disabledBlockEvent = extraAllowedEventNames
      ? filterBlockedData.includes(rowDataName)
      : showBlocked;
    const disabled = disabledMappedData
      ? disabledMappedEvent || disabledBlockEvent || extraDisabledEventName
      : disabledBlockEvent || extraDisabledEventName;

    if (searchText) {
      const escapeSearchText = escapeRegExp(searchText);
      const regex = new RegExp(`(${escapeSearchText})`, 'gi');
      const parts = rowDataName.split(regex);
      const labelContent = parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} style={{ fontWeight: 500, color: 'rgba(0,0,0,0.88)' }}>
            {part}
          </span>
        ) : (
          part
        ),
      );

      return {
        value: rowDataName,
        label: (
          <ItemOption className={`${disabled ? 'disabled' : ''}`}>
            <ItemOptionContent>
              {showEllipsis ? (
                <AddEllipsisWithTooltip
                  text={labelContent}
                  tooltipText={rowDataName}
                  dataIndex="name"
                />
              ) : (
                labelContent
              )}
            </ItemOptionContent>
            <div>
              {showMapped && <StatusTag>Mapped</StatusTag>}
              {showBlocked && <StatusTag>Blocked</StatusTag>}
            </div>
          </ItemOption>
        ),
        disabled: disabled,
      };
    } else {
      return {
        value: rowDataName,
        label: (
          <ItemOption className={`${disabled ? 'disabled' : ''}`}>
            <ItemOptionContent>
              {showEllipsis ? (
                <AddEllipsisWithTooltip
                  text={rowDataName}
                  tooltipText={rowDataName}
                  dataIndex="name"
                />
              ) : (
                rowDataName
              )}
            </ItemOptionContent>
            <div>
              {showMapped && <StatusTag>Mapped</StatusTag>}
              {showBlocked && <StatusTag>Blocked</StatusTag>}
            </div>
          </ItemOption>
        ),
        disabled: disabled,
      };
    }
  });
};
